import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Dashboard,
  ListAlt,
  GroupWork,
  LightMode,
  DarkMode,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  RateReview,
  ImageSearch as ImageSearchIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  DeleteSweep as DeleteSweepIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import axios from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '../App';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

const VENDOR_COLORS = {
  upload: '#6366f1',
  dashboard: '#06b6d4',
  navigation: '#10b981',
  groups: '#f59e0b',
  revisions: '#8b5cf6',
  audit: '#f43f5e',
  users: '#6366f1',
};

const navItems = [
  { path: '/', label: 'Upload PDFs', icon: CloudUpload, key: 'upload', description: 'Process new documents' },
  { path: '/dashboard', label: 'Dashboard', icon: Dashboard, key: 'dashboard', description: 'View all purchase orders' },
  { path: '/navigation', label: 'Filtering', icon: ListAlt, key: 'navigation', description: 'Filter line items' },
  { path: '/groups', label: 'Groups', icon: GroupWork, key: 'groups', description: 'Manage PO groups' },
  { path: '/revisions', label: 'Revisions', icon: HistoryIcon, key: 'revisions', description: 'PO revision history' },
];

function NavItem({ item, isActive, isCollapsed, onClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const color = VENDOR_COLORS[item.key];
  const Icon = item.icon;

  const button = (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isCollapsed ? 0 : 1.5,
        px: isCollapsed ? 0 : 2,
        py: 1.25,
        mx: isCollapsed ? 'auto' : 1.5,
        borderRadius: '10px',
        cursor: 'pointer',
        width: isCollapsed ? 44 : 'auto',
        height: 44,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        position: 'relative',
        transition: 'all 0.15s ease',
        backgroundColor: isActive
          ? isDark
            ? `${color}18`
            : `${color}12`
          : 'transparent',
        '&:hover': {
          backgroundColor: isActive
            ? isDark ? `${color}22` : `${color}16`
            : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        },
        '&::before': isActive
          ? {
              content: '""',
              position: 'absolute',
              left: isCollapsed ? '50%' : 0,
              top: isCollapsed ? '50%' : '25%',
              transform: isCollapsed ? 'translate(-50%,-50%)' : 'translateX(0)',
              width: isCollapsed ? 3 : 3,
              height: isCollapsed ? '40%' : '50%',
              borderRadius: '0 3px 3px 0',
              backgroundColor: color,
            }
          : {},
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '9px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: isActive
            ? `${color}20`
            : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
          transition: 'all 0.15s ease',
        }}
      >
        <Icon
          sx={{
            fontSize: 18,
            color: isActive
              ? color
              : isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)',
            transition: 'color 0.15s ease',
          }}
        />
      </Box>
      {!isCollapsed && (
        <Box>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive
                ? isDark ? '#e2e8f0' : '#111111'
                : isDark ? 'rgba(226,232,240,0.6)' : 'rgba(0,0,0,0.55)',
              lineHeight: 1.2,
              transition: 'all 0.15s ease',
            }}
          >
            {item.label}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={item.label} placement="right" arrow>
        {button}
      </Tooltip>
    );
  }
  return button;
}

function SidebarContent({ open, onToggle, toggleTheme, currentTheme, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isCollapsed = !open;
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSnack, setAuditSnack] = useState('');

  // Flush DB state
  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [flushLoading, setFlushLoading] = useState(false);
  const [flushSnack, setFlushSnack] = useState('');
  const [flushSnackSeverity, setFlushSnackSeverity] = useState('success');
  const { user, isAdmin, logout } = useAuth();

  const handleFlushDB = async () => {
    try {
      setFlushLoading(true);
      await axios.delete(`${API_BASE_URL}/api/flush-db`);
      setFlushDialogOpen(false);
      setFlushSnackSeverity('success');
      setFlushSnack('Database flushed successfully. All PO data has been cleared.');
    } catch (err) {
      setFlushSnackSeverity('error');
      setFlushSnack(err.response?.data?.detail || 'Flush failed. Please try again.');
    } finally {
      setFlushLoading(false);
    }
  };

  // Style Image Viewer state
  const [imgStyleInput, setImgStyleInput] = useState('');
  const [imgSearched, setImgSearched] = useState('');
  const [imgModalOpen, setImgModalOpen] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const imgDebounceRef = useRef(null);

  // Auto-fetch image 600ms after user stops typing
  useEffect(() => {
    clearTimeout(imgDebounceRef.current);
    if (!imgStyleInput.trim()) {
      setImgSearched('');
      setImgLoading(false);
      return;
    }
    imgDebounceRef.current = setTimeout(() => {
      setImgLoading(true);
      setImgSearched(imgStyleInput.trim());
    }, 600);
    return () => clearTimeout(imgDebounceRef.current);
  }, [imgStyleInput]);

  // Only show image viewer on filtering and groups pages
  const showImageViewer = location.pathname === '/navigation' || location.pathname === '/groups';

  const handleNav = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleStartAudit = async () => {
    try {
      setAuditLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/purchase-orders/`, {
        params: { status: 'review', sort_by: 'id', sort_order: 'desc', limit: 100 },
      });
      const pos = res.data?.data || [];
      if (pos.length === 0) {
        setAuditSnack('No POs are pending review right now.');
        return;
      }
      const batch = pos.map((p) => p.id);
      const poNumberMap = Object.fromEntries(
        pos.map(p => [String(p.id), p.customer_po_number || String(p.id)])
      );
      navigate(`/audit/${batch[0]}`, { state: { processedPoIds: batch, poNumberMap } });
      if (onClose) onClose();
    } catch {
      setAuditSnack('Could not load POs. Is the API running?');
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDark ? '#0a1120' : '#ffffff',
        borderRight: `1px solid ${isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.08)'}`,
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
      }}
    >
      {/* Header / Logo */}
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          px: isCollapsed ? 1 : 2.5,
          flexShrink: 0,
          borderBottom: `1px solid ${isDark ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.06)'}`,
        }}
      >
        {!isCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.875rem', fontFamily: '"DM Sans", sans-serif' }}>
                B
              </Typography>
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  letterSpacing: '-0.02em',
                  color: isDark ? '#e2e8f0' : '#0f172a',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                Bernardo
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  letterSpacing: '0.05em',
                  color: isDark ? 'rgba(148,163,184,0.6)' : 'rgba(100,116,139,0.8)',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                PO Parser
              </Typography>
            </Box>
          </Box>
        )}
        {isCollapsed && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '9px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
              cursor: 'pointer',
            }}
            onClick={onToggle}
          >
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.875rem', fontFamily: '"DM Sans", sans-serif' }}>
              B
            </Typography>
          </Box>
        )}
        {!isCollapsed && (
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              color: isDark ? 'rgba(148,163,184,0.6)' : 'rgba(100,116,139,0.7)',
              '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
              borderRadius: '8px',
              width: 30,
              height: 30,
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Nav label */}
      {!isCollapsed && (
        <Box sx={{ px: 2.5, pt: 2.5, pb: 0.5 }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: isDark ? 'rgba(148,163,184,0.4)' : 'rgba(100,116,139,0.5)',
            }}
          >
            Main Menu
          </Typography>
        </Box>
      )}

      {/* Nav items */}
      <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            isActive={location.pathname === item.path}
            isCollapsed={isCollapsed}
            onClick={() => handleNav(item.path)}
          />
        ))}
        {isAdmin && (
          <NavItem
            item={{ path: '/users', label: 'Manage Users', icon: PeopleIcon, key: 'users', description: 'Register SSO users' }}
            isActive={location.pathname === '/users'}
            isCollapsed={isCollapsed}
            onClick={() => handleNav('/users')}
          />
        )}
      </Box>

      {/* ── Style Image Viewer (only on filtering & groups pages) ── */}
      {showImageViewer && (isCollapsed ? (
        /* Collapsed: just a small icon button that opens the modal directly */
        <Box sx={{ px: 1, pb: 1, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="Style Image Viewer" placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => setImgModalOpen(true)}
              sx={{
                width: 44, height: 40, borderRadius: '9px',
                color: isDark ? 'rgba(148,163,184,0.55)' : 'rgba(100,116,139,0.6)',
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                border: `1px solid ${isDark ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.08)'}`,
                '&:hover': { bgcolor: 'rgba(99,102,241,0.1)', color: '#818cf8', borderColor: 'rgba(99,102,241,0.3)' },
              }}
            >
              <ImageSearchIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        /* Expanded: full inline viewer */
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Divider sx={{ mb: 1.5, borderColor: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)' }} />
          <Typography sx={{
            fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: isDark ? 'rgba(148,163,184,0.4)' : 'rgba(100,116,139,0.5)',
            mb: 1,
          }}>
            Style Image
          </Typography>

          {/* Input row */}
          <Box sx={{ mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter Bernardo Style #"
              value={imgStyleInput}
              onChange={e => setImgStyleInput(e.target.value)}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.8rem', py: '6px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': { borderColor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#818cf8' },
                },
              }}
            />
          </Box>

          {/* Image preview */}
          {imgSearched ? (
            <Box
              onClick={() => !imgLoading && setImgModalOpen(true)}
              sx={{
                height: 150, borderRadius: '10px', overflow: 'hidden',
                border: `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.1)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                cursor: imgLoading ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
                position: 'relative',
                '&:hover': { opacity: imgLoading ? 1 : 0.85, borderColor: imgLoading ? undefined : 'rgba(99,102,241,0.4)' },
              }}
            >
              {imgLoading && (
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                  bgcolor: isDark ? 'rgba(10,17,32,0.7)' : 'rgba(255,255,255,0.7)',
                  zIndex: 2, borderRadius: '10px',
                }}>
                  <CircularProgress size={28} sx={{ color: '#818cf8' }} />
                  <Typography sx={{ fontSize: '0.68rem', color: isDark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.8)' }}>
                    Loading…
                  </Typography>
                </Box>
              )}
              <img
                key={imgSearched}
                src={`https://vportal.bernardomfg.com/api/altimage/${encodeURIComponent(imgSearched)}`}
                alt={`Style ${imgSearched}`}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: imgLoading ? 'none' : 'block' }}
                onLoad={() => setImgLoading(false)}
                onError={e => {
                  setImgLoading(false);
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300x200.png?text=Not+Found';
                  e.target.style.display = 'block';
                }}
              />
            </Box>
          ) : (
            <Box sx={{
              height: 90, borderRadius: '10px',
              border: `1px dashed ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.1)'}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            }}>
              <ImageSearchIcon sx={{ fontSize: 22, color: isDark ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.3)' }} />
              <Typography sx={{ fontSize: '0.68rem', color: isDark ? 'rgba(148,163,184,0.35)' : 'rgba(100,116,139,0.45)' }}>
                Enter a Style # above
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* Full-size image modal */}
      <Dialog open={imgModalOpen} onClose={() => setImgModalOpen(false)} maxWidth="lg" fullWidth>
        <Box sx={{ position: 'relative', bgcolor: '#181c24', p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <IconButton onClick={() => setImgModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
            <CloseIcon />
          </IconButton>
          {imgSearched && (
            <img
              src={`https://vportal.bernardomfg.com/api/altimage/${encodeURIComponent(imgSearched)}`}
              alt={`Style ${imgSearched}`}
              style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          )}
        </Box>
      </Dialog>

      {/* flex spacer to push Audit + bottom controls down */}
      <Box sx={{ flex: 1 }} />

      {/* Flush DB button */}
      {isAdmin && <Box sx={{ flexShrink: 0, px: isCollapsed ? 1 : 1.5, pb: 1 }}>
        {isCollapsed ? (
          <Tooltip title="Flush Database" placement="right" arrow>
            <Box
              onClick={() => setFlushDialogOpen(true)}
              sx={{
                width: 44, height: 40, borderRadius: '9px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
                border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)'}`,
                color: '#ef4444',
                mx: 'auto',
                transition: 'all 0.15s ease',
                '&:hover': { background: 'rgba(239,68,68,0.15)', borderColor: '#ef4444' },
              }}
            >
              <DeleteSweepIcon sx={{ fontSize: 17 }} />
            </Box>
          </Tooltip>
        ) : (
          <Box
            onClick={() => setFlushDialogOpen(true)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 1.5, py: 1.1, borderRadius: '9px',
              background: isDark ? 'rgba(239,68,68,0.07)' : 'rgba(239,68,68,0.05)',
              border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)'}`,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                background: 'rgba(239,68,68,0.13)',
                borderColor: 'rgba(239,68,68,0.45)',
              },
            }}
          >
            <Box sx={{
              width: 30, height: 30, borderRadius: '8px',
              background: 'linear-gradient(135deg, #ef4444, #f87171)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, boxShadow: '0 3px 8px rgba(239,68,68,0.3)',
            }}>
              <DeleteSweepIcon sx={{ fontSize: 15, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#fca5a5' : '#dc2626', lineHeight: 1.2 }}>
                Flush DB
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: isDark ? 'rgba(252,165,165,0.6)' : 'rgba(220,38,38,0.6)', lineHeight: 1.1 }}>
                Clear all PO data
              </Typography>
            </Box>
          </Box>
        )}
      </Box>}

      {/* Flush DB confirmation dialog */}
      <Dialog open={flushDialogOpen} onClose={() => !flushLoading && setFlushDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <DeleteSweepIcon sx={{ color: '#ef4444' }} />
          Flush Database
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will permanently delete <strong>all purchase order data</strong> from the database:
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 1 }}>
            {['Documents', 'Purchase orders', 'Line items', 'PO revisions', 'Groups', 'Bamboo Rose exports'].map(t => (
              <Typography key={t} component="li" variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{t}</Typography>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: '#10b981', fontWeight: 600 }}>
            ✓ Vendor rule sets will be preserved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, color: '#ef4444', fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setFlushDialogOpen(false)} disabled={flushLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleFlushDB}
            disabled={flushLoading}
            startIcon={flushLoading ? <CircularProgress size={14} color="inherit" /> : <DeleteSweepIcon />}
          >
            {flushLoading ? 'Flushing…' : 'Yes, Flush DB'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit POs button */}
      <Box sx={{ flexShrink: 0, px: isCollapsed ? 1 : 1.5, pb: 1.5 }}>
        {isCollapsed ? (
          <Tooltip title="Audit POs — start reviewing pending orders" placement="right" arrow>
            <Box
              onClick={handleStartAudit}
              sx={{
                width: 44,
                height: 40,
                borderRadius: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #f43f5e22, #fb718518)',
                border: '1px solid rgba(244,63,94,0.3)',
                color: '#f43f5e',
                mx: 'auto',
                transition: 'all 0.15s ease',
                position: 'relative',
                '&:hover': { background: 'linear-gradient(135deg, #f43f5e30, #fb718528)', borderColor: '#f43f5e' },
              }}
            >
              {auditLoading
                ? <CircularProgress size={15} sx={{ color: '#f43f5e' }} />
                : <RateReview sx={{ fontSize: 17 }} />}
            </Box>
          </Tooltip>
        ) : (
          <Box
            onClick={handleStartAudit}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.1,
              borderRadius: '9px',
              background: 'linear-gradient(135deg, rgba(244,63,94,0.1), rgba(251,113,133,0.06))',
              border: '1px solid rgba(244,63,94,0.25)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(244,63,94,0.17), rgba(251,113,133,0.12))',
                borderColor: 'rgba(244,63,94,0.5)',
              },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #f43f5e, #fb7185)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 3px 8px rgba(244,63,94,0.35)',
              }}
            >
              {auditLoading
                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                : <RateReview sx={{ fontSize: 15, color: '#fff' }} />}
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#fda4af' : '#e11d48', lineHeight: 1.2 }}>
                Audit POs
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: isDark ? 'rgba(253,164,175,0.6)' : 'rgba(225,29,72,0.6)', lineHeight: 1.1 }}>
                Review pending orders
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* No-POs snackbar */}
      <Snackbar
        open={!!auditSnack}
        autoHideDuration={3500}
        onClose={() => setAuditSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setAuditSnack('')} sx={{ fontSize: '0.8125rem' }}>
          {auditSnack}
        </Alert>
      </Snackbar>

      {/* Flush DB result snackbar */}
      <Snackbar
        open={!!flushSnack}
        autoHideDuration={4000}
        onClose={() => setFlushSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={flushSnackSeverity} onClose={() => setFlushSnack('')} sx={{ fontSize: '0.8125rem' }}>
          {flushSnack}
        </Alert>
      </Snackbar>

      {/* User + logout */}
      <Box sx={{ flexShrink: 0, px: isCollapsed ? 1 : 1.5, pb: 1 }}>
        {isCollapsed ? (
          <Tooltip title={user?.email || 'Sign out'} placement="right" arrow>
            <Box onClick={logout} sx={{
              width: 44, height: 40, borderRadius: '9px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', mx: 'auto',
              border: `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.1)'}`,
              color: isDark ? 'rgba(226,232,240,0.7)' : 'rgba(15,23,42,0.7)',
              '&:hover': { borderColor: '#ef4444', color: '#ef4444' },
            }}>
              <LogoutIcon sx={{ fontSize: 17 }} />
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 1, borderRadius: '9px',
            border: `1px solid ${isDark ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.08)'}`,
          }}>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.full_name || user?.email}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.2 }}>
                {user?.role}
              </Typography>
            </Box>
            <Tooltip title="Sign out" arrow>
              <Box onClick={logout} sx={{ cursor: 'pointer', display: 'flex', p: 0.5, borderRadius: '6px', '&:hover': { color: '#ef4444' } }}>
                <LogoutIcon sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Bottom controls */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${isDark ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.06)'}`,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          gap: 1,
        }}
      >
        {!isCollapsed && (
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: isDark ? 'rgba(148,163,184,0.35)' : 'rgba(100,116,139,0.45)',
              letterSpacing: '0.04em',
            }}
          >
            v2.0
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={currentTheme === 'dark' ? 'Light Mode' : 'Dark Mode'} placement="top" arrow>
            <IconButton
              onClick={toggleTheme}
              size="small"
              sx={{
                color: isDark ? 'rgba(148,163,184,0.6)' : 'rgba(100,116,139,0.7)',
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', color: isDark ? '#e2e8f0' : '#0f172a' },
                borderRadius: '8px',
                width: 32,
                height: 32,
                transition: 'all 0.15s ease',
              }}
            >
              {currentTheme === 'dark' ? <LightMode sx={{ fontSize: 17 }} /> : <DarkMode sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
          {isCollapsed && (
            <Tooltip title="Expand sidebar" placement="right" arrow>
              <IconButton
                onClick={onToggle}
                size="small"
                sx={{
                  color: isDark ? 'rgba(148,163,184,0.6)' : 'rgba(100,116,139,0.7)',
                  '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronRight sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const Sidebar = ({ open, isMobile, onToggle, onClose, toggleTheme, currentTheme }) => {
  const theme = useTheme();

  if (isMobile) {
    return (
      <>
        {/* Mobile hamburger in corner */}
        <Box
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1300,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              borderRadius: '9px',
              width: 38,
              height: 38,
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>
        <Drawer
          anchor="left"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              border: 'none',
              backgroundImage: 'none',
              backgroundColor: theme.palette.mode === 'dark' ? '#0a1120' : '#ffffff',
            },
          }}
        >
          <SidebarContent
            open={true}
            onToggle={onToggle}
            toggleTheme={toggleTheme}
            currentTheme={currentTheme}
            onClose={onClose}
          />
        </Drawer>
      </>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 1200,
        width: open ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: { xs: 'none', md: 'block' },
      }}
    >
      <SidebarContent
        open={open}
        onToggle={onToggle}
        toggleTheme={toggleTheme}
        currentTheme={currentTheme}
      />
    </Box>
  );
};

export default Sidebar;
